/* eslint-disable react/prop-types */

import { createColumnHelper } from "@tanstack/react-table";
import { StyledModal } from "Components/Common/StyledModal";
import { StyledTable } from "Components/Common/StyledTable";
import { DeleteIcon } from "Components/Icons/DeleteIcon";
import { EllipsisVerticalIcon } from "Components/Icons/EllipsisVerticalIcon";
import { CoursesCatalogStore, CurriculumMapping, CustomOptionType, TeachingMethods } from "Stores/courseStore";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { DomainCompetenciesModal } from "./DomainCompetenciesModal";
import { observer } from "mobx-react";
import { InputMultiSelectWCustomOptions } from "Components/Common/InputMultiSelectWCustomOptions";
import { InputSelectWCustomOptions } from "Components/Common/InputSelectWCustomOptions";
import { useToast } from "@/hooks/useToast";
import { Defaults } from "@/common/utils";

interface CurriculumMappingSubTableProps {
    initialData: CurriculumMapping[];
    description: string;
    editMode: boolean;
    course_ruuid: string;
    setEditMode: () => void;
    coursesCatalogStore?: CoursesCatalogStore;

}

export type MappingProperty = "CLO" | "CognitiveLevel" | "DomainCompetency" | "TeachingMethod";

export const labelMap = {
    CLO: "Course Learning Outcome",
    CognitiveLevel: "Cognitive Level",
    TeachingMethod: "Teaching Method",
    DomainCompetency: "Domain Competency"
  };

const TableCellSelect = ({ getValue, column, row }) => {
    const initialValue = getValue();
    const columnMeta = column.columnDef.meta;
    const isEditing = columnMeta?.editMode;
    const property: MappingProperty = columnMeta.property;
    const coursesStore: CoursesCatalogStore = columnMeta.store;
    const isMultiSelect = Array.isArray(initialValue);
    const [value, setValue] = useState(initialValue);
    const [errorDelete, setErrorDelete] = useState(false);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);


    const displayValue = useMemo(() => {
        if (isMultiSelect && Array.isArray(value as TeachingMethods)) {
            // if (value.length === 0) return "-"
            
            return value?.map((item) => {
                if (item && item.teaching_method) {
                    return item.teaching_method.method_name;
                }
                if (item && item.cognitive_level) {
                    return item.cognitive_level.cognitive_level_name;
                }
                return ""
            }).join(", ");
        }
        if (value && typeof value === "object") {
            return value;
        }
        return value || "";
    }, [value]);

    const onCreate = async (e: string) => {
        if (property === "CLO") {
            await coursesStore.createCLO({
                name: e,
            });
            coursesStore.fetchCLOs();
        } else if (property === "CognitiveLevel") {
            await coursesStore.createCognitiveLevel({
                name: e,
            });
            coursesStore.fetchCognitiveLevels();
        } else if (property === "TeachingMethod") {
            await coursesStore.createTeachingMethod({
                name: e,
            });
            coursesStore.fetchTeachingMethods();
        }
    };

    const onDelete = async (e: string) => {
        if (property === "CLO") {
            await coursesStore.deleteCLO(e);
            if (coursesStore.isErrorDeleteLabel) {
                setErrorDelete(true);
            } else {
                coursesStore.fetchCLOs();
            }
        } else if (property === "CognitiveLevel") {
            await coursesStore.deleteCognitiveLevel(e);
            if (coursesStore.isErrorDeleteLabel) {
                setErrorDelete(true);
            } else {
                coursesStore.fetchCognitiveLevels();
            }
        } else if (property === "TeachingMethod") {
            await coursesStore.deleteTeachingMethod(e);
            if (coursesStore.isErrorDeleteLabel) {
                setErrorDelete(true);
            } else {
                coursesStore.fetchTeachingMethods();
            }
        }
    };

    const onChangeStatus = async (
        e: CustomOptionType,
        newStatus: "archived" | "active"
    ) => {
        const newStatusItem: CustomOptionType = {
            name: e.name,
            status: newStatus,
        };

        if (property === "CLO") {
            await coursesStore.updateCLO(newStatusItem, e.ruuid!);
            coursesStore.fetchCLOs();
        } else if (property === "CognitiveLevel") {
            await coursesStore.updateCognitiveLevel(newStatusItem, e.ruuid!);
            coursesStore.fetchCognitiveLevels();
        } else if (property === "TeachingMethod") {
            await coursesStore.updateTeachingMethod(newStatusItem, e.ruuid!);
            coursesStore.fetchTeachingMethods();
        }
    };

    const onEdit = async (e: CustomOptionType) => {
        if (property === "CLO") {
            await coursesStore.updateCLO(e, e.ruuid!);
            coursesStore.fetchCLOs();
        } else if (property === "CognitiveLevel") {
            await coursesStore.updateCognitiveLevel(e, e.ruuid!);
            coursesStore.fetchCognitiveLevels();
        } else if (property === "TeachingMethod") {
            await coursesStore.updateTeachingMethod(e, e.ruuid!);
            coursesStore.fetchTeachingMethods();
        }
    };
    
    const getValueAsRuuids = (value) => {
        if (!Array.isArray(value)) {
            return typeof value === "string" ? [value] : [];
        }
        // get ruuid for teaching method or cognitive levels
        return value
            .filter(item => item && typeof item === 'object')
            .map(item => {
                if (item && item.teaching_method) {
                    return item.teaching_method.ruuid;
                }
                if (item && item.cognitive_level) {
                    return item.cognitive_level.ruuid;
                }
                return undefined
            }).filter(Boolean);
    };

    const transformedValue = getValueAsRuuids(value);

    if (isEditing) {
        if (property === "DomainCompetency") {
            return (
                <DomainCompetenciesModal
                    competencies={coursesStore.domainCompetencies}
                    isLoading={coursesStore.isLoadingMapping}
                    onSelect={(newValue: string) => {
                        columnMeta.onSelect(newValue, row.original.ruuid);
                    }}
                    value={row.original.domain_competency?.standard_name}
                />
            );
        }

        if (["TeachingMethod", "CognitiveLevel"].includes(property)) {
            return (
                <InputMultiSelectWCustomOptions
                    property={columnMeta.property}
                    name={columnMeta?.name}
                    value={transformedValue}
                    placeHolder={columnMeta?.placeHolder}
                    options={columnMeta?.options}
                    onCreate={onCreate}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onChangeStatus={onChangeStatus}
                    onSelect={(newValues: string[]) => {
                        columnMeta.onSelect(newValues, row.original.ruuid);
                    }}
                    isLoading={coursesStore.isLoadingMapping}
                    deleteError={errorDelete}
                    closeDeleteError={() => setErrorDelete(false)}
                />
            );
        }

        return (
            <InputSelectWCustomOptions
                property={columnMeta.property}
                name={columnMeta?.name}
                value={value ?? ""}
                placeHolder={columnMeta?.placeHolder}
                options={columnMeta?.options}
                onCreate={onCreate}
                onEdit={onEdit}
                onDelete={onDelete}
                onChangeStatus={onChangeStatus}
                onSelect={(newValue: string) => {
                    columnMeta.onSelect(newValue, row.original.ruuid);
                }}
                isLoading={coursesStore.isLoadingMapping}
                deleteError={errorDelete}
                closeDeleteError={() => setErrorDelete(false)}
            />
        );
    }

    return displayValue;
};


const TableCellAction = ({ row, column }) => {
    const columnMeta = column.columnDef.meta;
    const editMode = columnMeta?.editMode;
    const setMappingId = columnMeta?.setMappingId;
    const removeTmp = columnMeta?.removeTmp;
    const disable = row.original.curriculum_mapping_clos_ruuid &&
        row.original.domain_competency_ruuid &&
        row.original.cognitive_level_ruuid &&
        row.original.teaching_method_ruuid;

    return (editMode ?
        <DeleteIcon
            onClick={() => {
                disable && !row.original.ruuid.includes("tmpCM") ?
                setMappingId(row.original.ruuid) :
                removeTmp(row.original.ruuid);
            }}
            className={(!disable && !row.original.ruuid.includes("tmpCM")) ? "editMappingIconDisable" : "editMappingIcon"}
        /> :
            <Dropdown drop="start">
                <Dropdown.Toggle id="dropdown-mapping-action">
                    <EllipsisVerticalIcon className={"drop-ellipsis-icon"}/>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => {
                        setMappingId(row.original.ruuid);
                    }}>
                        Delete <DeleteIcon />
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        );
};

export const CurriculumMappingSubTable = observer(({ initialData, description, editMode, coursesCatalogStore, course_ruuid, setEditMode }: CurriculumMappingSubTableProps) => {
    const [mappingId, setMappingId] = useState<undefined | string>(undefined);
    const [renderableData, setRenderableData] = useState<Array<CurriculumMapping>>(initialData);
    const [courseDescription, setCourseDescription] = useState<string>(description);
    const [curriculumPageSize, setCurriculumPageSize] = useState(Defaults.tablePageSize());

    const { toast } = useToast();

    const buttonsValidation = () => {
        let isValid = true;
        renderableData?.filter((e) => e.mode).forEach((item) => {
            if(
                item.curriculum_mapping_clos_ruuid === "adding" ||
                item.domain_competency_ruuid === "" ||
                item.teaching_method_ruuid === "adding"
            ) {
                isValid = false;
            }
        });
        return isValid;
    };

    const renderTitle = (title: string) => {
        return (
            <div style={{ display: "flex", gap: 4 }}>
                {title}
            </div>
        );
    };

    const generateUniqueId = () => {
        const now = new Date().getTime();
        const maxPostgresInt = 2147483647;
        return `tmpCM-${now % maxPostgresInt}`;
    };

    const onSubmit = () => {
        renderableData.forEach(async (e) => {
            e.mode === "create" ? (
                await coursesCatalogStore?.createMapping(e),
                coursesCatalogStore?.fetchCoursesCatalog()
            ) :
            e.mode === "edit" && (
                await coursesCatalogStore?.updateCurriculumMapping({...e, description: "curriculum mapping updated"}, e.ruuid),
                coursesCatalogStore?.fetchCoursesCatalog()
            );
        });
    };

    const onDeleteMapping = async (e: string) => {
        await coursesCatalogStore?.deleteMapping(e);
        coursesCatalogStore?.fetchCoursesCatalog();
        setMappingId(undefined);
    };

    const removeTmp = (e: string) => {
        setRenderableData(prev => prev.filter((item) => item.ruuid !== e));
    };

    const onNewDescription = async () => {
        await coursesCatalogStore?.addCourseDescription(
            course_ruuid,
            {description: courseDescription, update_comment: "course description updated"}
        );
        coursesCatalogStore?.fetchCoursesCatalog();
    };

    useEffect(() => {
        !editMode && (
            setRenderableData(initialData),
            setCourseDescription(description)
        );
    });

    useEffect(() => {
        if (coursesCatalogStore?.isError) {
            toast({
                type: "success",
                title: "Course Catalog Error",
                message: coursesCatalogStore?.errorMessage || "An error occurred while processing the request, please try again later"
            })
            setTimeout(() => {
                coursesCatalogStore.clearError();
            }, 5000);
        }
    }, [coursesCatalogStore?.isError]);

    const columnHelper = createColumnHelper<CurriculumMapping>();

    const columns = [
        columnHelper.accessor(editMode ? "curriculum_mapping_clos.ruuid" : "curriculum_mapping_clos.clo", {
            header: () => renderTitle("Course Learning Outcome"),
            cell: TableCellSelect,
            meta: {
                options: coursesCatalogStore?.CLOs,
                name: "course_learning_outcome",
                label: "Course Learning Outcome",
                placeHolder: "Add a Course Learning Outcome",
                editMode: editMode,
                store: coursesCatalogStore,
                property: "CLO" as MappingProperty,
                onSelect: (e: string, id: string) => {
                    setRenderableData(prevRenderable =>
                        prevRenderable.map(item =>
                            item.ruuid === id?
                                { ...item, curriculum_mapping_clos_ruuid: e, mode: id.includes("tmp") ? "create" : "edit" }
                                : item
                        )
                    );
                }
            },
        }),
        columnHelper.accessor("domain_competency.standard_name", {
            header: () => renderTitle("Domain Competency"),
            cell: TableCellSelect,
            meta: {
                options: coursesCatalogStore?.domainCompetencies,
                name: "domain_competency",
                label: "Domain Competency",
                placeHolder: "Add a Domain Competency",
                editMode: editMode,
                store: coursesCatalogStore,
                property: "DomainCompetency" as MappingProperty,
                onSelect: (e: string, id: string) => {
                    setRenderableData(prevRenderable =>
                        prevRenderable.map(item =>
                            item.ruuid === id?
                                { ...item, domain_competency_ruuid: e, mode: id.includes("tmp") ? "create" : "edit" }
                                : item
                        )
                    );
                }
            }
        }),
        columnHelper.accessor("cognitive_levels", {
            header: () => renderTitle("Cognitive Level"),
            cell: TableCellSelect,
            meta: {
                options: coursesCatalogStore?.cognitiveLevels,
                name: "cognitive_level",
                label: "Cognitive level",
                placeHolder: "Add a Cognitive Level",
                editMode: editMode,
                store: coursesCatalogStore,
                property: "CognitiveLevel" as MappingProperty,
                onSelect: (e: string, id: string) => {
                    setRenderableData(prevRenderable =>
                        prevRenderable.map(item =>
                            item.ruuid === id?
                                { ...item, cognitive_level_ruuid: e, mode: id.includes("tmp") ? "create" : "edit" }
                                : item
                        )
                    );
                }
            },
        }),
        columnHelper.accessor("teaching_methods", {
            header: () => renderTitle("Teaching Method"),
            cell: TableCellSelect,
            meta: {
                options: coursesCatalogStore?.teachingMethods,
                name: "teaching_method",
                label: "Teaching Method",
                placeHolder: "Add a Teaching Method",
                editMode: editMode,
                store: coursesCatalogStore,
                property: "TeachingMethod" as MappingProperty,
                onSelect: (e: string, id: string) => {
                    setRenderableData(prevRenderable =>
                        prevRenderable.map(item =>
                            item.ruuid === id?
                                { ...item, teaching_method_ruuid: e, mode: id.includes("tmp") ? "create" : "edit" }
                                : item
                        )
                    );
                }
            }
        }),
        columnHelper.accessor("ruuid", {
            header: () => renderTitle(""),
            cell: TableCellAction,
            meta: {
                editMode: editMode,
                setMappingId: setMappingId,
                removeTmp: removeTmp
            }
        })
    ];
    return (
        <div className="curriculum-mapping-expandable">
            <div className={editMode ? "description-input-container" : "description-container"}>
                {editMode ?
                    <Form.Control
                        value={courseDescription ?? ""}
                        onChange={(e) => {
                            setCourseDescription(e.target.value);
                        }}
                        type="text"
                        as={"input"}
                        placeholder="Course Description"
                    /> :
                    `Description: ${courseDescription === "" ? "No description available" : courseDescription}`
                }
            </div>
            <StyledTable
                defaultData={renderableData ?? []}
                columns={columns}
                className="curriculum-mapping-table"
                noDataMessage="No mappings were found"
                pageSize={curriculumPageSize}
                setPageSize={setCurriculumPageSize}
            />
            {editMode &&
                <div className="mapping-buttons">
                    <Button variant="secondary"
                        onClick={() => {setRenderableData(
                            renderableData.concat([
                                {ruuid: generateUniqueId(),
                                course_catalog_rid: course_ruuid,
                                curriculum_mapping_clos_ruuid: "",
                                domain_competency_ruuid: "",
                                cognitive_level_ruuid: "",
                                teaching_method_ruuid: "",
                                status: "active",
                                }
                            ])
                        );}}
                        className="add-mapping-button"
                        disabled={!buttonsValidation()}
                    >
                        + Add mapping
                    </Button>
                    <Button
                        variant="secondary"
                        disabled={(
                            buttonsValidation() ?
                                description === courseDescription ?
                                !(
                                    (renderableData?.some((e) => e.mode === "create") && buttonsValidation()) ||
                                    renderableData?.some((e) => e.mode === "edit")
                                )
                                : false
                            : true
                        )}
                        onClick={() => {
                            description !== courseDescription && onNewDescription(),
                            onSubmit(),
                            setEditMode();
                        }}>
                        Save changes
                    </Button>
                </div>
            }
            <StyledModal
                isOpen={mappingId !== undefined}
                onClose={() => setMappingId(undefined)}
                title={"Are you sure you want to delete Mapping?"}
                subtitle={"This action cannot be undone"}
                cancelButtonLabel="Cancel"
                confirmButtonLabel={"Delete"}
                onSubmit={(e) => {
                    e.preventDefault();
                    onDeleteMapping(mappingId!);
                }}
            />
        </div>
    );
});
